import { Pencil } from 'lucide-react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import type { DataTableColumnDef } from '@nucleus-commerce/admin-kit/index-table';
import type { ExchangeRate } from '@nucleus-commerce/api-client/platform';
import { useFormat } from '@nucleus-commerce/i18n/react';
import { Button } from '@nucleus-commerce/ui/components/button';

import { Mono } from '@/shared/ui/mono';

import { pairLabel } from '../api/mappers';

import { RateSourceBadge } from './rate-source-badge';

export interface ExchangeRateColumnsOptions {
  /** Null for roles that only read; the actions column is then omitted. */
  onUpdate: ((rate: ExchangeRate) => void) | null;
}

const RATE_DIGITS = { minimumFractionDigits: 4, maximumFractionDigits: 6 } as const;

/**
 * Column model for the exchange rates table. Sortable by pair and fetched time; the pair's
 * value is `USD/EUR`, which is also what search matches currency codes against.
 */
export function useExchangeRateColumns({ onUpdate }: ExchangeRateColumnsOptions) {
  const { t } = useTranslation('exchangeRates');
  const format = useFormat();

  return useMemo<Array<DataTableColumnDef<ExchangeRate>>>(() => {
    const columns: Array<DataTableColumnDef<ExchangeRate>> = [
      {
        id: 'pair',
        accessorFn: (row) => `${row.baseCurrency}/${row.quoteCurrency}`,
        header: t('columns.pair'),
        meta: { sortField: 'pair', filterKey: 'baseCurrency', lockVisibility: true },
        cell: ({ row }) => (
          <Mono className="text-foreground font-medium">{pairLabel(row.original)}</Mono>
        ),
      },
      {
        id: 'rate',
        accessorKey: 'rate',
        header: t('columns.rate'),
        enableSorting: false,
        meta: { align: 'end', searchable: false },
        cell: ({ row }) => (
          <Mono className="text-foreground">{format.number(row.original.rate, RATE_DIGITS)}</Mono>
        ),
      },
      {
        id: 'source',
        accessorKey: 'source',
        header: t('columns.source'),
        enableSorting: false,
        meta: { filterKey: 'source', searchable: false, tour: 'platform.exchange-rates.source' },
        cell: ({ row }) => <RateSourceBadge source={row.original.source} />,
      },
      {
        id: 'fetchedAt',
        accessorKey: 'fetchedAt',
        header: t('columns.fetchedAt'),
        sortFn: 'datetime',
        meta: { sortField: 'fetchedAt', searchable: false },
        cell: ({ row }) => (
          <span className="text-muted-foreground" title={format.utc(row.original.fetchedAt)}>
            {format.relative(row.original.fetchedAt)}
          </span>
        ),
      },
    ];
    if (onUpdate) {
      columns.push({
        id: 'actions',
        header: () => <span className="sr-only">{t('columns.actions')}</span>,
        enableSorting: false,
        enableHiding: false,
        meta: { label: t('columns.actions'), align: 'end', searchable: false },
        cell: ({ row }) => (
          <Button
            variant="ghost"
            size="sm"
            aria-label={t('actions.updateFor', { pair: pairLabel(row.original) })}
            onClick={() => onUpdate(row.original)}
          >
            <Pencil aria-hidden />
            {t('actions.update')}
          </Button>
        ),
      });
    }
    return columns;
  }, [format, onUpdate, t]);
}
